import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiFileText, FiPlus, FiCalendar, FiArrowRight, FiCheckCircle, FiCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getNotices, pinNotice } from '../../services/api';
import { PageHeader } from '../../components/common';
import { NOTICE_CATEGORIES } from './noticesConfig';
import styles from './NoticeBoardPage.module.css';

const NoticeBoardPage = () => {
  const { activeDerasarId, isDerasarAdmin } = useAuth();
  const navigate = useNavigate();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pinning, setPinning] = useState(null);

  const loadNotices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotices(activeDerasarId);
      setNotices(res.data);
    } catch {
      toast.error('Failed to load notices');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => {
    if (!activeDerasarId) return;
    loadNotices();
  }, [activeDerasarId, loadNotices]);

  const filtered = selectedCategory
    ? notices.filter((n) => n.category === selectedCategory)
    : notices;

  const getCategoryLabel = (cat) =>
    NOTICE_CATEGORIES.find((c) => c.value === cat)?.label || cat || 'Uncategorized';

  const getCategoryColor = (cat) =>
    NOTICE_CATEGORIES.find((c) => c.value === cat)?.color || '#6b7280';

  const handlePin = async (e, id) => {
    e.stopPropagation();
    setPinning(id);
    try {
      await pinNotice(id);
      const res = await getNotices(activeDerasarId);
      setNotices(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update pin');
    } finally {
      setPinning(null);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={styles.page}>
      <PageHeader
        icon={<FiFileText />}
        title="Notice Board"
        actions={
          isDerasarAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate('create')}>
              <FiPlus /> Post Notice
            </button>
          )
        }
      />

      {NOTICE_CATEGORIES.length > 0 && (
        <div className={styles.filterRow}>
          <select
            className={styles.filterSelect}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {NOTICE_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <FiFileText size={40} />
          <p>No notices at this time.</p>
        </div>
      ) : (
        <div className={styles.noticesList}>
          {filtered.map((notice) => (
            <div
              key={notice.id}
              className={`${styles.noticeCard} ${notice.category === 'dashboard' ? styles.noticeCardDashboard : notice.isPinned ? styles.noticeCardPinned : ''}`}
              onClick={() => navigate(`${notice.id}`)}
            >
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>{notice.title}</div>
                <div className={styles.cardBadges}>
                  {notice.isPinned && (
                    <span className={styles.pinnedChip}>
                      <FiCheckCircle size={10} /> Pinned
                    </span>
                  )}
                  {notice.category && (
                    <span
                      className={styles.categoryBadge}
                      style={{ backgroundColor: getCategoryColor(notice.category) }}
                    >
                      {getCategoryLabel(notice.category)}
                    </span>
                  )}
                  {isDerasarAdmin && (
                    <button
                      className={styles.pinBtn}
                      onClick={(e) => handlePin(e, notice.id)}
                      disabled={pinning === notice.id}
                      title={notice.isPinned ? 'Unpin from dashboard' : 'Pin to dashboard'}
                    >
                      {notice.isPinned
                        ? <FiCheckCircle size={15} className={styles.pinBtnActive} />
                        : <FiCircle size={15} className={styles.pinBtnInactive} />
                      }
                    </button>
                  )}
                </div>
              </div>

              <div
                className={styles.cardContent}
                dangerouslySetInnerHTML={{ __html: notice.content }}
              />

              <div className={styles.cardFooter}>
                <div className={styles.cardMeta}>
                  <FiCalendar size={12} />
                  {new Date(notice.createdAt).toLocaleDateString('en-IN')}
                </div>
                {notice.expiryDate && (
                  <div className={styles.cardMeta}>
                    Expires: {new Date(notice.expiryDate).toLocaleDateString('en-IN')}
                  </div>
                )}
                <div className={styles.cardAction}>Read More <FiArrowRight size={14} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isDerasarAdmin && notices.length > 0 && (
        <div className={styles.footerAction}>
          <button className="btn btn-ghost" onClick={() => navigate('manage')}>
            Manage Notices
          </button>
        </div>
      )}
    </div>
  );
};

export default NoticeBoardPage;
